import { AlloyLegacyThemeDesignTokens, OverlayDesignTokens } from '../types';
import { boxShadowLayer } from './common';
import type {
  Weave3FlyoutInput,
  Weave3TooltipInput,
} from '../types/weave3Types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

export const getOverlayTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): OverlayDesignTokens => ({
  light: {
    'background-color': theme.tokens.colors.background.primary.value,
    'text-color': theme.tokens.colors.text.primary.value,
    'border-color': theme.tokens.colors.background.primary.value,
    'border-radius': theme.borderRadius.medium,
    'box-shadow': theme.shadows.highElevation,
  },
  medium: {
    'background-color': theme.tokens.colors.background.inverse.selected.value,
    'text-color': theme.tokens.colors.text.primary.value,
    'border-color': theme.tokens.colors.background.inverse.selected.value,
    'border-radius': theme.borderRadius.medium,
    'box-shadow': theme.shadows.lowElevation,
  },
  dark: {
    'background-color': theme.tokens.colors.background.inverse.subtle.value,
    'text-color': theme.tokens.colors.text.inverse.value,
    'border-color': theme.tokens.colors.background.inverse.subtle.value,
    'border-radius': theme.borderRadius.small,
    'link-color': theme.tokens.colors.text.link.inverse.value,
  },
  default: {
    'background-color': theme.tokens.colors.background.inverse.subtle.value,
    'text-color': theme.tokens.colors.text.inverse.value,
    'border-color': theme.tokens.colors.background.inverse.subtle.value,
    'border-radius': theme.borderRadius.small,
    'link-color': theme.tokens.colors.text.link.inverse.value,
  },
});

export const getOverlayTokensWeave = (
  tokens: WeaveTokens,
): OverlayDesignTokens => {
  const tooltip = tokens?.component?.tooltip;
  const popover = tokens?.component?.popover;
  const white = tokens?.generic?.color?.white;
  const charcoal = tokens?.generic?.color?.charcoal;
  const darkGray = tokens?.generic?.color?.['dark-gray'];

  const defaultStyle = {
    'background-color': tooltip?.['background-color'],
    'text-color': tooltip?.['text-color'],
    'border-color': tooltip?.['background-color'],
    'border-radius': tooltip?.['border-radius'],
  };

  const darkStyle = {
    'background-color': darkGray?.[100]?.[100],
    'text-color': charcoal?.[100]?.[100],
    'border-color': darkGray?.[100]?.[100],
    'border-radius': tooltip?.['border-radius'],
  };

  const mediumStyle = {
    'background-color': charcoal?.[100]?.[100],
    'text-color': charcoal?.[900]?.[100],
    'border-color': charcoal?.[100]?.[100],
    'border-radius': tooltip?.['border-radius'],
  };

  const lightStyle = {
    'background-color': white?.[100],
    'text-color': charcoal?.[900]?.[100],
    'border-color': white?.[100],
    'border-radius': tooltip?.['border-radius'],
    'box-shadow': boxShadowLayer(
      popover?.['box-shadow-x'],
      popover?.['box-shadow-y'],
      popover?.['box-shadow-blur'],
      popover?.['box-shadow-color'],
    ),
  };

  return {
    light: lightStyle,
    medium: mediumStyle,
    dark: darkStyle,
    default: defaultStyle,
  };
};

/** Alloy's `medium` variant has no W3 counterpart, so its colours stay literal (plan §0). */
const MEDIUM_FALLBACK = {
  backgroundColor: '#dcdcdc',
  textColor: '#3c3c3c',
  boxShadow: '0 1px 6px rgba(12, 44, 84, 0.12)',
} as const;

/**
 * W3 ships no `popover`, so `light`'s elevation composes two `flyout` shadow
 * layers. `dark` and `default` both map to the inverting `tooltip.$root` step —
 * Alloy defines them identically — and take their `link-color` from their own
 * `text-color`, since `$root` flips polarity per theme and W3 ships no tooltip
 * link colour (DSGN-2473).
 */
export const getOverlayTokensWeave3 = (
  tooltip?: Weave3TooltipInput,
  flyout?: Weave3FlyoutInput,
): OverlayDesignTokens => {
  const borderRadius = tooltip?.['border-radius'];
  const background = tooltip?.['background-color'];
  const text = tooltip?.['text-color'];

  const shadowOne = flyout?.['box-shadow-one'];
  const shadowTwo = flyout?.['box-shadow-two'];
  const elevation =
    [
      boxShadowLayer(
        shadowOne?.x,
        shadowOne?.y,
        shadowOne?.blur,
        shadowOne?.spread,
        shadowOne?.color,
      ),
      boxShadowLayer(
        shadowTwo?.x,
        shadowTwo?.y,
        shadowTwo?.blur,
        shadowTwo?.spread,
        shadowTwo?.color,
      ),
    ]
      .filter(Boolean)
      .join(', ') || undefined;

  const invertedStyle = {
    'background-color': background?.$root,
    'text-color': text?.$root,
    'border-color': background?.$root,
    'border-radius': borderRadius,
    'box-shadow': undefined,
    'link-color': text?.$root,
  };

  return {
    light: {
      'background-color': background?.light,
      'text-color': text?.light,
      'border-color': background?.light,
      'border-radius': borderRadius,
      'box-shadow': elevation,
      'link-color': undefined,
    },
    medium: {
      'background-color': MEDIUM_FALLBACK.backgroundColor,
      'text-color': MEDIUM_FALLBACK.textColor,
      'border-color': MEDIUM_FALLBACK.backgroundColor,
      'border-radius': borderRadius,
      'box-shadow': MEDIUM_FALLBACK.boxShadow,
      'link-color': undefined,
    },
    dark: invertedStyle,
    default: invertedStyle,
  };
};
